import React from 'react';
import { Navigate, useLoaderData, useSearchParams } from 'react-router-dom';
import { Spinner } from '@oysterjs/ui/spinner';
import { UsageCycle, AgencyUsageFieldsFragment } from '~/api/graphql';
import { defaultUsCanadaTimezone } from '~/lib/agency-timezones';
import { useCanViewSettingsUsageTab } from '~/components/use-settings-usage-tab-enabled';
import { ChartGranularity, UsagePeriodChart } from '~/components/settings/usage/usage-period-chart';
import { UsageHistory } from '~/components/settings/usage/usage-history';
import { UsageMeters } from '~/components/settings/usage/usage-meters';
import { UsageOverageEstimate } from '~/components/settings/usage/usage-overage-estimate';
import { UsageOverageInvoices } from '~/components/settings/usage/usage-overage-invoices';
import { UsagePeriodHeader } from '~/components/settings/usage/usage-period-header';
import { Await } from '@oysterjs/ui/async';

export const SettingsUsagePage = () => {
  // Usage metering fields resolve against Stripe, so they are fetched here on demand
  // rather than in the app bootstrap (GetAgencyUser). This page only renders once the
  // Usage tab is enabled, so a hidden tab never triggers the Stripe-backed resolver.
  const loader = useLoaderData() as
    | {
        data: Promise<{
          agencyUsage: AgencyUsageFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <React.Suspense
      fallback={
        <div className="flex justify-center py-4">
          <Spinner size="size-6" color="neutral" />
        </div>
      }
    >
      <Await resolve={loader?.data}>
        {(data) => data?.agencyUsage && <SettingsUsagePageInner agency={data.agencyUsage} />}
      </Await>
    </React.Suspense>
  );
};

const SettingsUsagePageInner = ({ agency }: { agency: AgencyUsageFieldsFragment }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const timezone = searchParams.get('timezone') || defaultUsCanadaTimezone();
  const subscription = agency?.subscription;
  const period = subscription?.currentUsagePeriod;
  const report = subscription?.currentUsageReport;
  const users = agency?.users ?? [];

  const chartGranularity: ChartGranularity =
    period?.usageCycle === UsageCycle.Annual ? 'weekly' : 'daily';

  React.useEffect(() => {
    if (searchParams.has('timezone')) {
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('timezone', defaultUsCanadaTimezone());
        return next;
      },
      { replace: true }
    );
  }, [searchParams, setSearchParams]);

  const onTimezoneChange = (nextTimezone: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('timezone', nextTimezone);
        return next;
      },
      { replace: true }
    );
  };

  return (
    <div className="space-y-6">
      <UsagePeriodHeader period={period} timezone={timezone} />
      <UsageMeters report={report} allowances={subscription?.currentUsageAllowances} />
      <UsagePeriodChart
        report={report}
        allowances={subscription?.currentUsageAllowances}
        period={period}
        timezone={timezone}
        onTimezoneChange={onTimezoneChange}
        chartGranularity={chartGranularity}
      />
      <UsageHistory timezone={timezone} users={users} />
      {subscription?.currentOverageBillEstimate && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <UsageOverageEstimate estimate={subscription.currentOverageBillEstimate} />
          <UsageOverageInvoices timezone={timezone} />
        </div>
      )}
    </div>
  );
};

export const SettingsUsageRoute = () => {
  const canViewUsageTab = useCanViewSettingsUsageTab();
  if (!canViewUsageTab) {
    return <Navigate to="/app/settings/profile" replace />;
  }
  return <SettingsUsagePage />;
};
